import React, { useEffect, useState } from 'react';
import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  DateField,
  TextInput,
  Filter,
  FunctionField,
  useRecordContext,
  Show,
  SimpleShowLayout,
  SelectInput,
  useDataProvider,
  TabbedShowLayout,
  Tab,
  useGetList,
} from 'react-admin';
import { Chip, Typography, Box, Tabs, Tab as MuiTab, Card, CardContent, Grid } from '@mui/material';
import { Link } from 'react-router-dom';

const OculusSubscriptionFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Buscar ID o Owner" source="q" alwaysOn />
    <TextInput label="SKU" source="sku" />
    <TextInput label="User Name" source="username" />
    <TextInput label="Tipo de Trial" source="trial_type" />
    <SelectInput
      label="Estado"
      source="status"
      choices={[
        { id: 'all', name: 'Todas' },
        { id: 'active', name: 'Activas' },
        { id: 'trial', name: 'Trial' },
        { id: 'inactive', name: 'Inactivas' },
      ]}
      alwaysOn
    />
  </Filter>
);

const StatusChip = () => {
  const record = useRecordContext();
  if (!record) return null;

  let label = 'Inactiva';
  let color = 'error';

  if (record.is_trial) {
    label = 'Trial';
    color = 'warning';
  } else if (record.is_active) {
    label = 'Activa';
    color = 'success';
  }

  return <Chip label={label} color={color} size="small" />;
};

const UserLinkField = ({ source, label }) => {
  const record = useRecordContext();
  if (!record || !record.userId) return <span>-</span>;
  
  return (
    <Link 
      to={`/users/${record.userId}/show`}
      style={{ 
        textDecoration: 'none', 
        color: '#1976d2',
        fontWeight: 500,
        '&:hover': {
          textDecoration: 'underline'
        }
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {record[source] || '-'}
    </Link>
  );
};

const formatPrice = (price, currency) =>
  !price || price === 0 ? 'Gratis' : `${currency} ${parseFloat(price).toFixed(2)}`;

const ActiveSubscriptionsKPI = () => {
  const dataProvider = useDataProvider();
  const [count, setCount] = useState(null);

  useEffect(() => {
    dataProvider
      .getList('oculus', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'is_active', order: 'DESC' },
        filter: {},
      })
      .then(({ data }) => {
        const activeCount = data.filter(record => record.is_active === true).length;
        setCount(activeCount);
      })
      .catch(() => setCount(0));
  }, [dataProvider]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" color="primary.main">
        Suscripciones Activas: {count !== null ? count : 'Cargando...'}
      </Typography>
    </Box>
  );
};

const TotalPriceKPI = () => {
  const dataProvider = useDataProvider();
  const [totalPrice, setTotalPrice] = useState(null);

  useEffect(() => {
    dataProvider
      .getList('oculus', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'subscription_id', order: 'ASC' },
        filter: {},
      })
      .then(({ data }) => {
        const sum = data.reduce((acc, record) => {
          const price = parseFloat(record.current_price);
          return acc + (isNaN(price) ? 0 : price);
        }, 0);
        setTotalPrice(sum);
      })
      .catch(() => setTotalPrice(0));
  }, [dataProvider]);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="h6" color="primary.main">
        Total Precio Actual: {totalPrice !== null ? totalPrice.toFixed(2) : 'Cargando...'}
      </Typography>
    </Box>
  );
};

export const OculusSubscriptionsList = (props) => (
  <>
    <ActiveSubscriptionsKPI />
    <TotalPriceKPI />
    <List
      filters={<OculusSubscriptionFilter />}
      {...props}
      bulkActionButtons={false}
      sort={{ field: 'period_start_time', order: 'ASC' }}
      filterDefaultValues={{ status: 'all' }}
      perPage={25}
    >
      <Datagrid rowClick="show">
        <FunctionField
          label="User ID"
          render={record => <UserLinkField source="userId" label="User ID" />}
        />
        <FunctionField
          label="User Name"
          render={record => <UserLinkField source="username" label="User Name" />}
        />
        <TextField source="subscription_id" label="ID Suscripción" />
        <TextField source="owner_id" label="Owner ID" />
        <TextField source="sku" label="SKU" />
        <StatusChip label="Estado" />
        <TextField source="trial_type" label="Tipo de Trial" />
        <DateField source="period_start_time" label="Inicio Periodo" showTime />
        <DateField source="period_end_time" label="Fin Periodo" showTime />
        <FunctionField
          label="Precio Actual"
          render={record => formatPrice(record.current_price, record.current_currency)}
        />
        <FunctionField
          label="Próximo Precio"
          render={record => formatPrice(record.next_price, record.next_currency)}
        />
        <DateField source="next_renewal_time" label="Próxima Renovación" showTime />
        <DateField source="cancellation_time" label="Cancelación" showTime />
        <DateField source="updated_at" label="Última Actualización" showTime />
        <BooleanField source="is_active" label="Activa" />
        <BooleanField source="is_trial" label="Trial" />
      </Datagrid>
    </List>
  </>
);

export const OculusSubscriptionsShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <FunctionField
        label="User ID"
        render={record => <UserLinkField source="userId" label="User ID" />}
      />
      <FunctionField
        label="Username"
        render={record => <UserLinkField source="username" label="Username" />}
      />
      <TextField source="subscription_id" label="ID Suscripción" />
      <TextField source="owner_id" label="Owner ID" />
      <TextField source="sku" label="SKU" />
      <BooleanField source="is_active" label="Activa" />
      <BooleanField source="is_trial" label="Trial" />
      <TextField source="trial_type" label="Tipo de Trial" />
      <DateField source="period_start_time" label="Inicio Periodo" showTime />
      <DateField source="period_end_time" label="Fin Periodo" showTime />
      <FunctionField
        label="Precio Actual"
        render={record => formatPrice(record.current_price, record.current_currency)}
      />
      <FunctionField
        label="Próximo Precio"
        render={record => formatPrice(record.next_price, record.next_currency)}
      />
      <DateField source="next_renewal_time" label="Próxima Renovación" showTime />
      <DateField source="cancellation_time" label="Cancelación" showTime />
      <DateField source="updated_at" label="Última Actualización" showTime />
    </SimpleShowLayout>
  </Show>
);

const SubscriptionTypeChip = ({ sku }) => {
  const getTypeFromSku = (sku) => {
    if (sku.includes('ANNUAL')) return { label: 'Anual', color: 'success' };
    if (sku.includes('MONTHLY')) return { label: 'Mensual', color: 'primary' };
    if (sku.includes('QUARTERLY')) return { label: 'Trimestral', color: 'info' };
    if (sku.includes('SEMIANNUAL')) return { label: 'Semestral', color: 'warning' };
    if (sku.includes('Assets')) return { label: 'Contenido', color: 'secondary' };
    return { label: 'Otro', color: 'default' };
  };

  const { label, color } = getTypeFromSku(sku);
  return <Chip label={label} color={color} size="small" />;
};


const AvailablePurchasesList = () => {
  const { data, isLoading, error } = useGetList('available_purchases');

  console.log('AvailablePurchasesList - data:', data);
  console.log('AvailablePurchasesList - isLoading:', isLoading);
  console.log('AvailablePurchasesList - error:', error);

  if (isLoading) return <div>Cargando productos disponibles...</div>;
  if (error) {
    console.error('Error completo:', error);
    return <div>Error al cargar los productos disponibles: {error.message}</div>;
  }
  
  if (!data || data.length === 0) {
    return <div>No hay productos disponibles</div>;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Productos Disponibles ({data?.length || 0})
      </Typography>
      
      <Grid container spacing={2}>
        {data?.map((product) => (
          <Grid item xs={12} md={6} lg={4} key={product.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                    {product.current_offer_name}
                  </Typography>
                  <SubscriptionTypeChip sku={product.sku} />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {product.current_offer_description}
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
                    {product.current_offer_formatted}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    SKU: {product.sku}
                  </Typography>
                </Box>

                {product.trial_offers && product.trial_offers.length > 0 && (
                  <Box sx={{ mb: 2, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                      🎁 Prueba Gratuita Disponible
                    </Typography>
                    <Typography variant="caption" color="success.dark">
                      {product.trial_offers[0].trial_term} - {product.trial_offers[0].trial_formatted}
                    </Typography>
                  </Box>
                )}

                {product.billing_plans && product.billing_plans.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Plan de facturación: {product.billing_plans[0].paid_offer_formatted}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export const OculusMain = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <div>
      <Tabs value={tabValue} onChange={handleTabChange} aria-label="oculus tabs">
        <MuiTab label="Suscripciones Activas" />
        <MuiTab label="Productos Disponibles" />
      </Tabs>
      
      {tabValue === 0 && (
        <Box sx={{ mt: 2 }}>
          <OculusSubscriptionsList />
        </Box>
      )}
      
      {tabValue === 1 && (
        <Box sx={{ mt: 2 }}>
          <AvailablePurchasesList />
        </Box>
      )}
    </div>
  );
};